import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, FileText, FolderPlus, Pencil, Plus, Square, SquareCheck, Trash2, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useAddOutlineSection, useDeleteOutlineSection } from '../../../../hooks/useOutline';
import { useConfirm } from '../shared/ConfirmDialog';
import { flattenSections, useOutlineSections, useReorderOutlineSections, useUpdateOutlineSection } from './workingOutlineHooks';
/**
 * Volume Table-of-Contents — section tree with full CRUD + multi-select.
 *
 * Selection:
 *   - Click checkbox → toggle single section
 *   - Cmd/Ctrl+click → toggle without deselecting others
 *   - Shift+click → range select from last clicked to current
 *   - "All" button → select everything
 *
 * Deletion:
 *   - Single section via hover trash icon
 *   - Bulk via "Delete (N)" in select mode
 *   - If a parent is selected, warns that all children will be deleted too
 */ export function VolumeTOC({ projectId, volumeId }) {
    const { sectionId: activeSectionId } = useParams();
    const outline = useOutlineSections(projectId, volumeId);
    const addSection = useAddOutlineSection(projectId, volumeId);
    const deleteSection = useDeleteOutlineSection(projectId, volumeId);
    const updateSection = useUpdateOutlineSection(projectId, volumeId);
    const reorder = useReorderOutlineSections(projectId, volumeId);
    const confirm = useConfirm();
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const renaming = useRef(false);
    const [saveError, setSaveError] = useState('');
    const [retryOrder, setRetryOrder] = useState(null);
    const ordering = useRef(false);
    const [collapsed, setCollapsed] = useState(new Set());
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [deleting, setDeleting] = useState(false);
    const lastClickedRef = useRef(null);
    // Build flat list preserving tree structure.
    const flat = [];
    for (const top of outline.data ?? []){
        flattenWithCounts(top, 0, flat, collapsed);
    }
    // All section IDs in flat order (for range select).
    const allIds = flat.map((s)=>s.sectionId);
    // Full tree (not collapsed) for counting descendants of selected parents.
    const fullFlat = [];
    for (const top of outline.data ?? []){
        flattenWithCounts(top, 0, fullFlat, new Set()); // never collapsed
    }
    // --- Handlers ---
    const saveOrder = async (ids)=>{
        if (ordering.current) return;
        ordering.current = true;
        setRetryOrder(ids);
        try {
            await reorder.mutateAsync(ids);
            setRetryOrder(null);
        } catch  {
        // Keep the requested order for an explicit retry after partial writes.
        } finally{
            ordering.current = false;
        }
    };
    const siblingsFor = (id)=>{
        const sections = flattenSections(outline.data ?? []);
        const section = sections.find((node)=>node.sectionId === id);
        return sections.filter((node)=>node.parentSectionId === section?.parentSectionId);
    };
    const moveSection = (id, direction)=>{
        const ids = siblingsFor(id).map((section)=>section.sectionId);
        const index = ids.indexOf(id);
        const next = index + direction;
        if (index < 0 || next < 0 || next >= ids.length) return;
        [ids[index], ids[next]] = [
            ids[next],
            ids[index]
        ];
        void saveOrder(ids);
    };
    const handleAdd = ()=>{
        addSection.mutate({
            title: 'New section'
        });
    };
    const handleAddChild = (parentId)=>{
        addSection.mutate({
            parentSectionId: parentId,
            title: 'New subsection'
        });
        setCollapsed((prev)=>{
            const next = new Set(prev);
            next.delete(parentId);
            return next;
        });
    };
    const startRename = (sectionId, currentTitle)=>{
        setEditingId(sectionId);
        setEditValue(currentTitle);
    };
    const saveRename = useCallback(async ()=>{
        if (!editingId || renaming.current || !editValue.trim()) return;
        renaming.current = true;
        setSaveError('');
        try {
            await updateSection.mutateAsync({
                sectionId: editingId,
                updates: {
                    title: editValue.trim()
                }
            });
            setEditingId(null);
        } catch  {
            setSaveError('Unable to rename the section. Your name is retained; try again.');
        } finally{
            renaming.current = false;
        }
    }, [
        editingId,
        editValue,
        updateSection
    ]);
    const toggleCollapse = (sectionId)=>{
        setCollapsed((prev)=>{
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId);
            else next.add(sectionId);
            return next;
        });
    };
    // --- Selection logic ---
    const handleCheckboxClick = (sectionId, event)=>{
        const isMod = event.metaKey || event.ctrlKey;
        const isShift = event.shiftKey;
        if (isShift && lastClickedRef.current) {
            // Range select: from lastClicked to current.
            const fromIdx = allIds.indexOf(lastClickedRef.current);
            const toIdx = allIds.indexOf(sectionId);
            if (fromIdx >= 0 && toIdx >= 0) {
                const start = Math.min(fromIdx, toIdx);
                const end = Math.max(fromIdx, toIdx);
                setSelected((prev)=>{
                    const next = new Set(prev);
                    for(let i = start; i <= end; i++){
                        next.add(allIds[i]);
                    }
                    return next;
                });
            }
        } else if (isMod) {
            // Toggle single without clearing others.
            setSelected((prev)=>{
                const next = new Set(prev);
                if (next.has(sectionId)) next.delete(sectionId);
                else next.add(sectionId);
                return next;
            });
        } else {
            // Plain click: toggle single.
            setSelected((prev)=>{
                const next = new Set(prev);
                if (next.has(sectionId)) next.delete(sectionId);
                else next.add(sectionId);
                return next;
            });
        }
        lastClickedRef.current = sectionId;
    };
    const selectAll = ()=>{
        setSelected(new Set(allIds));
    };
    // --- Delete logic ---
    const handleSingleDelete = async (sectionId, title, childCount)=>{
        const hasChildren = childCount > 0;
        const ok = await confirm({
            title: hasChildren ? `Delete "${title}" and its ${childCount} subsection${childCount === 1 ? '' : 's'}?` : `Delete "${title}"?`,
            description: hasChildren ? 'This section contains subsections. Deleting it will permanently remove the section and all of its contents, including every subsection underneath it.' : 'This will permanently remove this section and all of its contents.',
            variant: 'danger',
            confirmLabel: hasChildren ? `Delete section + ${childCount} subsection${childCount === 1 ? '' : 's'}` : 'Delete section'
        });
        if (ok) {
            deleteSection.mutate(sectionId);
        }
    };
    const handleBulkDelete = async ()=>{
        if (selected.size === 0) return;
        // Count how many extra children will be implicitly deleted.
        const selectedSet = new Set(selected);
        let implicitChildren = 0;
        const parentNames = [];
        for (const s of fullFlat){
            if (selectedSet.has(s.sectionId) && s.childCount > 0) {
                parentNames.push(s.title);
                // Count children that aren't already explicitly selected.
                const children = getDescendantIds(outline.data ?? [], s.sectionId);
                for (const childId of children){
                    if (!selectedSet.has(childId)) {
                        implicitChildren++;
                        selectedSet.add(childId); // they'll be deleted too
                    }
                }
            }
        }
        const totalToDelete = selectedSet.size;
        const hasParents = parentNames.length > 0;
        let description;
        if (hasParents && implicitChildren > 0) {
            description = /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "You selected ",
                            selected.size,
                            " section",
                            selected.size === 1 ? '' : 's',
                            ", but",
                            ' ',
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: parentNames.join(', ')
                            }),
                            ' ',
                            parentNames.length === 1 ? 'has' : 'have',
                            " subsections that will also be removed."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-slate-500",
                        children: [
                            "Total sections that will be deleted:",
                            ' ',
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold text-rose-600",
                                children: totalToDelete
                            }),
                            " (",
                            selected.size,
                            ' ',
                            "selected + ",
                            implicitChildren,
                            " subsection",
                            implicitChildren === 1 ? '' : 's',
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-slate-500",
                        children: "All content in these sections will be permanently lost."
                    })
                ]
            });
        } else {
            description = /*#__PURE__*/ _jsxs("p", {
                children: [
                    "This will permanently delete ",
                    totalToDelete,
                    " section",
                    totalToDelete === 1 ? '' : 's',
                    " and all of their contents. This cannot be undone."
                ]
            });
        }
        const ok = await confirm({
            title: `Delete ${totalToDelete} section${totalToDelete === 1 ? '' : 's'}?`,
            description,
            variant: 'danger',
            confirmLabel: `Delete ${totalToDelete} section${totalToDelete === 1 ? '' : 's'}`
        });
        if (!ok) return;
        setDeleting(true);
        // Delete in reverse order (children first) to avoid cascade issues.
        const orderedIds = fullFlat.filter((s)=>selectedSet.has(s.sectionId)).reverse().map((s)=>s.sectionId);
        for (const id of orderedIds){
            try {
                await deleteSection.mutateAsync(id);
            } catch  {
            // continue — parent deletion may have already cascaded
            }
        }
        setSelected(new Set());
        setSelectMode(false);
        setDeleting(false);
    };
    const exitSelectMode = ()=>{
        setSelectMode(false);
        setSelected(new Set());
        lastClickedRef.current = null;
    };
    return /*#__PURE__*/ _jsxs("fieldset", {
        disabled: reorder.isPending,
        className: "flex h-full min-w-0 flex-col border-r border-border bg-card",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-1 border-b border-slate-200 px-3 py-2.5",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-xs font-semibold uppercase tracking-wide text-slate-500",
                        children: "Sections"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center gap-1",
                        children: !selectMode ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                flat.length > 0 && /*#__PURE__*/ _jsx(Button, {
                                    size: "sm",
                                    variant: "ghost",
                                    onClick: ()=>setSelectMode(true),
                                    className: "h-7 px-1.5 text-xs text-slate-400 hover:text-slate-600",
                                    title: "Select sections",
                                    children: /*#__PURE__*/ _jsx(SquareCheck, {
                                        className: "h-3.5 w-3.5",
                                        "aria-hidden": "true"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Button, {
                                    size: "sm",
                                    variant: "ghost",
                                    "data-testid": Testids.proposalStructure.addSection,
                                    onClick: handleAdd,
                                    disabled: addSection.isPending,
                                    className: "h-7 gap-1 px-2 text-xs",
                                    title: "Add section",
                                    children: [
                                        /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "Add"
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: selectAll,
                                    className: "text-[10px] font-medium text-blue-600 hover:text-blue-800 px-1",
                                    children: "All"
                                }),
                                /*#__PURE__*/ _jsxs(Button, {
                                    size: "sm",
                                    variant: "ghost",
                                    onClick: handleBulkDelete,
                                    disabled: selected.size === 0 || deleting,
                                    className: "h-7 gap-1 px-2 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 disabled:text-slate-300",
                                    title: `Delete ${selected.size} selected`,
                                    children: [
                                        /*#__PURE__*/ _jsx(Trash2, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        selected.size > 0 ? `Delete (${selected.size})` : 'Delete'
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    size: "sm",
                                    variant: "ghost",
                                    onClick: exitSelectMode,
                                    className: "h-7 px-1.5 text-xs text-slate-400 hover:text-slate-600",
                                    title: "Cancel selection",
                                    children: /*#__PURE__*/ _jsx(X, {
                                        className: "h-3.5 w-3.5",
                                        "aria-hidden": "true"
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            (saveError || addSection.isError || deleteSection.isError || outline.isError) && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                className: "p-3",
                children: saveError || 'Unable to save or load the outline. Reload and try again.'
            }),
            reorder.isPending && /*#__PURE__*/ _jsx("output", {
                className: "p-3",
                children: "Saving section order…"
            }),
            reorder.isError && retryOrder && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                className: "p-3",
                children: [
                    "Section order could not be fully saved. The outline shows the saved order.",
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>void saveOrder(retryOrder),
                        disabled: reorder.isPending,
                        children: "Retry section order"
                    })
                ]
            }),
            selectMode && /*#__PURE__*/ _jsxs("div", {
                className: "border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] text-slate-400",
                children: [
                    "Click to select · Shift+click for range ·",
                    ' ',
                    navigator.platform.includes('Mac') ? '⌘' : 'Ctrl',
                    "+click to toggle"
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 overflow-auto p-2",
                children: outline.isLoading ? /*#__PURE__*/ _jsx("div", {
                    className: "space-y-1.5",
                    children: [
                        0,
                        1,
                        2,
                        3
                    ].map((i)=>/*#__PURE__*/ _jsx("div", {
                            className: "h-8 animate-shimmer rounded bg-slate-100"
                        }, i))
                }) : flat.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                    className: "px-2 py-6 text-center text-xs italic text-slate-500",
                    children: [
                        "No sections yet. Click ",
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-semibold text-slate-700",
                            children: "Add"
                        }),
                        " to create the first one."
                    ]
                }) : /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-0.5",
                    children: flat.map((s)=>{
                        const isSelected = selected.has(s.sectionId);
                        return /*#__PURE__*/ _jsxs("li", {
                            "data-testid": Testids.proposalStructure.sectionRow,
                            "data-section-id": s.sectionId,
                            className: "group relative flex items-center",
                            children: [
                                editingId === s.sectionId ? /*#__PURE__*/ _jsx("div", {
                                    className: "flex items-center gap-1 px-2 py-1",
                                    style: {
                                        paddingLeft: 8 + s.depth * 16
                                    },
                                    children: /*#__PURE__*/ _jsx("input", {
                                        type: "text",
                                        value: editValue,
                                        onChange: (e)=>setEditValue(e.target.value),
                                        disabled: updateSection.isPending,
                                        "aria-label": "Section name",
                                        "data-testid": Testids.proposalStructure.sectionName,
                                        onKeyDown: (e)=>{
                                            if (e.key === 'Enter') saveRename();
                                            if (e.key === 'Escape') setEditingId(null);
                                        },
                                        ref: (el)=>el?.focus(),
                                        className: "flex-1 rounded border border-blue-300 bg-white px-1.5 py-0.5 text-sm text-slate-900 outline-none ring-2 ring-blue-100"
                                    })
                                }) : /*#__PURE__*/ _jsxs("div", {
                                    className: "flex min-w-0 flex-1 items-center",
                                    children: [
                                        selectMode && /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: (e)=>handleCheckboxClick(s.sectionId, e),
                                            className: "flex-shrink-0 p-1 text-slate-400 hover:text-blue-600",
                                            style: {
                                                marginLeft: s.depth * 16
                                            },
                                            children: isSelected ? /*#__PURE__*/ _jsx(SquareCheck, {
                                                className: "h-3.5 w-3.5 text-blue-600"
                                            }) : /*#__PURE__*/ _jsx(Square, {
                                                className: "h-3.5 w-3.5"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs(NavLink, {
                                            to: `/proposal/${projectId}/v2/volume/${volumeId}/section/${s.sectionId}`,
                                            end: true,
                                            className: ({ isActive })=>`flex min-w-0 flex-1 items-start gap-1 rounded-md px-2 py-1.5 text-[13px] transition-colors ${isSelected ? 'bg-blue-50 text-blue-900' : isActive || activeSectionId === s.sectionId ? 'bg-blue-50/60 text-blue-800' : 'text-slate-700 hover:bg-slate-50'}`,
                                            style: {
                                                paddingLeft: selectMode ? 4 : 8 + s.depth * 16
                                            },
                                            children: [
                                                s.hasChildren ? /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    onClick: (e)=>{
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleCollapse(s.sectionId);
                                                    },
                                                    className: "mt-0.5 flex-shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-200",
                                                    children: collapsed.has(s.sectionId) ? /*#__PURE__*/ _jsx(ChevronRight, {
                                                        className: "h-3 w-3"
                                                    }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                                        className: "h-3 w-3"
                                                    })
                                                }) : /*#__PURE__*/ _jsx(FileText, {
                                                    className: "mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400",
                                                    "aria-hidden": "true"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "min-w-0 flex-1 truncate",
                                                    children: s.title
                                                }),
                                                s.childCount > 0 && /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-1 flex-shrink-0 text-[10px] text-slate-400",
                                                    children: s.childCount
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                editingId !== s.sectionId && !selectMode && /*#__PURE__*/ _jsxs("div", {
                                    className: "flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100",
                                    children: [
                                        [
                                            -1,
                                            1
                                        ].map((direction)=>{
                                            const siblings = siblingsFor(s.sectionId);
                                            const index = siblings.findIndex((node)=>node.sectionId === s.sectionId);
                                            return /*#__PURE__*/ _jsx(SpecraIconButton, {
                                                type: "button",
                                                variant: "ghost",
                                                size: "sm",
                                                "data-testid": direction === -1 ? Testids.proposalStructure.moveSectionUp : Testids.proposalStructure.moveSectionDown,
                                                label: `Move ${s.title} ${direction === -1 ? 'up' : 'down'}`,
                                                disabled: reorder.isPending || updateSection.isPending || (direction === -1 ? index === 0 : index === siblings.length - 1),
                                                onClick: ()=>moveSection(s.sectionId, direction),
                                                className: "disabled:opacity-30",
                                                children: direction === -1 ? /*#__PURE__*/ _jsx(ArrowUp, {
                                                    className: "h-3 w-3"
                                                }) : /*#__PURE__*/ _jsx(ArrowDown, {
                                                    className: "h-3 w-3"
                                                })
                                            }, direction);
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: (e)=>{
                                                e.preventDefault();
                                                e.stopPropagation();
                                                startRename(s.sectionId, s.title);
                                            },
                                            className: "flex h-6 w-6 items-center justify-center rounded text-slate-300 hover:bg-amber-50 hover:text-amber-600",
                                            title: "Rename section",
                                            children: /*#__PURE__*/ _jsx(Pencil, {
                                                className: "h-3 w-3"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>handleAddChild(s.sectionId),
                                            className: "flex h-6 w-6 items-center justify-center rounded text-slate-300 hover:bg-blue-50 hover:text-blue-600",
                                            title: "Add subsection",
                                            children: /*#__PURE__*/ _jsx(FolderPlus, {
                                                className: "h-3 w-3"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>handleSingleDelete(s.sectionId, s.title, s.childCount),
                                            className: "flex h-6 w-6 items-center justify-center rounded text-slate-300 hover:bg-rose-50 hover:text-rose-600",
                                            title: "Delete section",
                                            children: /*#__PURE__*/ _jsx(Trash2, {
                                                className: "h-3 w-3"
                                            })
                                        })
                                    ]
                                })
                            ]
                        }, s.sectionId);
                    })
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function flattenWithCounts(section, depth, out, collapsedSet) {
    const children = section.children ?? [];
    const hasChildren = children.length > 0;
    const childCount = countDescendants(section);
    out.push({
        sectionId: section.sectionId,
        title: section.title || '(untitled)',
        depth,
        hasChildren,
        childCount
    });
    if (hasChildren && !collapsedSet.has(section.sectionId)) {
        for (const child of children){
            flattenWithCounts(child, depth + 1, out, collapsedSet);
        }
    }
}
function countDescendants(section) {
    const children = section.children ?? [];
    let count = children.length;
    for (const child of children){
        count += countDescendants(child);
    }
    return count;
}
/** Get all descendant IDs of a section by sectionId from the outline tree. */ function getDescendantIds(sections, targetId) {
    for (const s of sections){
        if (s.sectionId === targetId) {
            return collectAllIds(s.children ?? []);
        }
        if (s.children) {
            const result = getDescendantIds(s.children, targetId);
            if (result.length > 0) return result;
        }
    }
    return [];
}
function collectAllIds(sections) {
    const out = [];
    for (const s of sections){
        out.push(s.sectionId);
        if (s.children) {
            out.push(...collectAllIds(s.children));
        }
    }
    return out;
}
